import React from 'react';
import { useDarkMode } from '../contexts/DarkModeContext';
import * as Sentry from "@sentry/react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    console.error('🚨 Error Boundary caught an error:', {
      errorId,
      error: error,
      errorInfo: errorInfo,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    // Capture error in Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      level: 'error',
      tags: {
        error_boundary: true,
        error_id: errorId,
      },
      extra: {
        errorInfo,
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: this.getUserId(),
      }
    });

    // Store error details in state
    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorId: errorId
    });

    // Send error to monitoring service (if available)
    this.sendErrorReport(error, errorInfo, errorId);
  }

  sendErrorReport = (error, errorInfo, errorId) => {
    try {
      // Send error to your monitoring service
      const errorReport = {
        errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: this.getUserId(), // Get from auth context if available
      };

      // Log to console for development
      console.error('📊 Error report prepared:', errorReport);

      // Send error to backend monitoring service
      const backendUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3000' 
        : 'https://trackable-gpt-backend.onrender.com';
      
      fetch(`${backendUrl}/errors/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorReport)
      }).then(response => {
        if (response.ok) {
          console.log('✅ Error report sent successfully');
        } else {
          console.error('❌ Failed to send error report:', response.status);
        }
      }).catch(err => {
        console.error('❌ Network error sending error report:', err);
      });

    } catch (reportError) {
      console.error('Failed to send error report:', reportError);
    }
  };

  getUserId = () => {
    try {
      // Try to get user ID from localStorage or auth context
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.user?.id || parsed.user?.email || 'anonymous';
      }
      return 'anonymous';
    } catch {
      return 'anonymous';
    }
  };

  render() {
    if (this.state.hasError) {
      // Error fallback UI
      return (
        <ErrorFallback 
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          onRetry={() => {
            this.setState({ hasError: false, error: null, errorInfo: null });
            window.location.reload();
          }}
        />
      );
    }

    return this.props.children;
  }
}

// Error fallback component
function ErrorFallback({ error, errorInfo, errorId, onRetry }) {
  const { isDarkMode } = useDarkMode();

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: isDarkMode ? 'var(--bg-primary)' : '#ffffff',
    color: isDarkMode ? 'var(--text-primary)' : '#333333'
  };

  const cardStyle = {
    maxWidth: '600px',
    padding: '2rem',
    borderRadius: '8px',
    backgroundColor: isDarkMode ? 'var(--bg-secondary)' : '#f8f9fa',
    border: `1px solid ${isDarkMode ? 'var(--border-color)' : '#dee2e6'}`,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  };

  const buttonStyle = {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    marginRight: '1rem',
    marginTop: '1rem'
  };

  const detailsStyle = {
    backgroundColor: isDarkMode ? 'var(--bg-tertiary)' : '#ffffff',
    border: `1px solid ${isDarkMode ? 'var(--border-color)' : '#dee2e6'}`,
    borderRadius: '4px',
    padding: '1rem',
    marginTop: '1rem',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
    maxHeight: '200px',
    overflow: 'auto'
  };

  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={{ color: '#dc3545', marginBottom: '1rem' }}>
          🚨 Oops! Something went wrong
        </h1>
        
        <p style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
          We're sorry, but an unexpected error occurred while loading this page.
        </p>

        <p style={{ marginBottom: '1rem', color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
          Error ID: <code style={{ 
            backgroundColor: isDarkMode ? 'var(--bg-tertiary)' : '#e9ecef',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '0.9rem'
          }}>
            {errorId}
          </code>
        </p>

        <div style={{ marginTop: '1.5rem' }}>
          <button 
            style={buttonStyle}
            onClick={onRetry}
          >
            🔄 Reload Page
          </button>
          
          <button 
            style={{
              ...buttonStyle,
              backgroundColor: isDarkMode ? 'var(--bg-tertiary)' : '#6c757d',
            }}
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? '🔼 Hide' : '🔽 Show'} Technical Details
          </button>
        </div>

        {showDetails && (
          <div style={detailsStyle}>
            <strong>Error:</strong> {error?.message}<br/>
            <strong>Location:</strong> {error?.stack?.split('\n')[1]?.trim()}<br/>
            <strong>Time:</strong> {new Date().toLocaleString()}<br/>
            <strong>URL:</strong> {window.location.href}<br/><br/>
            
            <details>
              <summary>Full Stack Trace</summary>
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                fontSize: '0.8rem',
                marginTop: '0.5rem' 
              }}>
                {error?.stack}
              </pre>
            </details>
          </div>
        )}

        <div style={{ 
          marginTop: '2rem', 
          padding: '1rem',
          backgroundColor: isDarkMode ? 'rgba(13, 110, 253, 0.1)' : 'rgba(13, 110, 253, 0.1)',
          borderRadius: '4px',
          fontSize: '0.9rem'
        }}>
          <strong>What can you do?</strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
            <li>Try refreshing the page</li>
            <li>Check your internet connection</li>
            <li>If the problem persists, please contact support with the Error ID above</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;